import { useState, useEffect, useCallback, useMemo } from 'react';
import API, { clearApiCache, getApiCacheStats, resolveMediaUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Heartbeat, Trash, ArrowClockwise, Database, HardDrives,
  Cpu, WhatsappLogo, TelegramLogo, Broom, CheckCircle, Warning,
  XCircle, Clock, ShieldCheck, FileText, Image as ImageIcon,
  VideoCamera, MusicNotes, MagnifyingGlass, ArrowSquareOut,
  DownloadSimple, Eye, ShieldWarning, Sparkle, Lightning
} from '@phosphor-icons/react';

export default function SystemHealthPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState('health');

  // Health state
  const [healthData, setHealthData] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [pingingTarget, setPingingTarget] = useState(null);
  const [pingResults, setPingResults] = useState({});

  // Cleanup stats state
  const [cleanupStats, setCleanupStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);
  const [retentionDays, setRetentionDays] = useState('all');
  const [keepOpenContacts, setKeepOpenContacts] = useState(true);
  const [deletingData, setDeletingData] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Media browser state
  const [mediaList, setMediaList] = useState([]);
  const [mediaTotal, setMediaTotal] = useState(0);
  const [mediaTotalBytes, setMediaTotalBytes] = useState(0);
  const [mediaTotalFormatted, setMediaTotalFormatted] = useState('0 B');
  const [mediaPage, setMediaPage] = useState(1);
  const [mediaTotalPages, setMediaTotalPages] = useState(1);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaCategory, setMediaCategory] = useState('all');
  const [mediaSource, setMediaSource] = useState('all');
  const [mediaSearch, setMediaSearch] = useState('');
  const [mediaSort, setMediaSort] = useState('newest');
  const [selectedMediaFiles, setSelectedMediaFiles] = useState([]);
  const [previewMediaModal, setPreviewMediaModal] = useState(null);
  const [confirmMediaDeleteOpen, setConfirmMediaDeleteOpen] = useState(false);
  const [deletingMedia, setDeletingMedia] = useState(false);

  // Cache state
  const [cacheStats, setCacheStats] = useState({ cachedRequests: 0, inFlightRequests: 0 });
  const [clearingCache, setClearingCache] = useState(false);

  // Load Health Data
  const fetchHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      const res = await API.get('/system/health', { noCache: true });
      setHealthData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch system health status');
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  // Load Cleanup Stats
  const fetchCleanupStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await API.get('/system/cleanup/stats', { noCache: true });
      setCleanupStats(res.data);
    } catch {
      toast.error('Failed to load data cleanup statistics');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // Load Media Files
  const fetchMedia = useCallback(async (page = 1) => {
    setLoadingMedia(true);
    try {
      const params = {
        category: mediaCategory,
        source: mediaSource,
        search: mediaSearch.trim(),
        sort: mediaSort,
        page,
        limit: 24,
      };
      const res = await API.get('/system/cleanup/media', { params, noCache: true });
      setMediaList(res.data.media || []);
      setMediaTotal(res.data.total || 0);
      setMediaTotalBytes(res.data.total_bytes || 0);
      setMediaTotalFormatted(res.data.total_formatted || '0 B');
      setMediaPage(res.data.page || 1);
      setMediaTotalPages(res.data.total_pages || 1);
    } catch {
      toast.error('Failed to load media files');
    } finally {
      setLoadingMedia(false);
    }
  }, [mediaCategory, mediaSource, mediaSearch, mediaSort]);

  // Update Cache stats
  const updateCacheMetrics = useCallback(() => {
    setCacheStats(getApiCacheStats());
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchHealth();
      updateCacheMetrics();
    }
  }, [isSuperAdmin, fetchHealth, updateCacheMetrics]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    if (activeTab === 'cleanup') {
      fetchCleanupStats();
    } else if (activeTab === 'media') {
      fetchMedia(1);
    } else if (activeTab === 'cache') {
      updateCacheMetrics();
    }
  }, [isSuperAdmin, activeTab, fetchCleanupStats, fetchMedia, updateCacheMetrics]);

  // Service Ping Test
  const handlePing = async (target) => {
    setPingingTarget(target);
    try {
      const res = await API.post('/system/health/ping', { target });
      setPingResults(prev => ({
        ...prev,
        [target]: res.data
      }));
      if (res.data.status === 'ok') {
        toast.success(`${target.toUpperCase()} responded in ${res.data.latency_ms}ms`);
      } else {
        toast.warning(`${target.toUpperCase()} warning: ${res.data.message || 'Check logs'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ping ${target}`);
      setPingResults(prev => ({
        ...prev,
        [target]: { status: 'error', error: err.message }
      }));
    } finally {
      setPingingTarget(null);
    }
  };

  // Toggle Cleanup Section selection
  const toggleSection = (key) => {
    setSelectedSections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSelectAllSections = () => {
    if (!cleanupStats?.sections) return;
    const allKeys = Object.keys(cleanupStats.sections);
    if (selectedSections.length === allKeys.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(allKeys);
    }
  };

  // Calculate items to delete
  const totalItemsToDelete = useMemo(() => {
    if (!cleanupStats?.sections) return 0;
    return selectedSections.reduce((sum, key) => {
      return sum + (cleanupStats.sections[key]?.count || 0);
    }, 0);
  }, [cleanupStats, selectedSections]);

  // Execute Data Cleanup
  const handleExecuteCleanup = async () => {
    if (selectedSections.length === 0) {
      return toast.warning('Please select at least one section to clean up');
    }
    setDeletingData(true);
    try {
      const payload = {
        sections: selectedSections,
        older_than_days: retentionDays === 'all' ? null : parseInt(retentionDays, 10),
        keep_open_contacts: keepOpenContacts,
      };
      const res = await API.post('/system/cleanup/delete', payload);
      toast.success(res.data.message || 'Data cleanup completed successfully');
      setConfirmDialogOpen(false);
      setSelectedSections([]);
      fetchCleanupStats();
      fetchHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Data cleanup failed');
    } finally {
      setDeletingData(false);
    }
  };

  // Toggle Media selection
  const toggleMediaFile = (name) => {
    setSelectedMediaFiles(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const handleSelectAllMediaOnPage = () => {
    const pageFilenames = mediaList.map(m => m.name);
    const allSelected = pageFilenames.every(f => selectedMediaFiles.includes(f));
    if (allSelected) {
      setSelectedMediaFiles(prev => prev.filter(f => !pageFilenames.includes(f)));
    } else {
      setSelectedMediaFiles(prev => Array.from(new Set([...prev, ...pageFilenames])));
    }
  };

  // Selected media size calculation
  const selectedMediaSizeFormatted = useMemo(() => {
    const bytes = mediaList
      .filter(m => selectedMediaFiles.includes(m.name))
      .reduce((sum, m) => sum + (m.size_bytes || 0), 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, [mediaList, selectedMediaFiles]);

  // Execute Media Delete
  const handleExecuteMediaDelete = async () => {
    if (selectedMediaFiles.length === 0) {
      return toast.warning('Please select at least one media file to delete');
    }
    setDeletingMedia(true);
    try {
      const payload = {
        sections: [],
        media_filenames: selectedMediaFiles,
        delete_all_media: false,
      };
      const res = await API.post('/system/cleanup/delete', payload);
      toast.success(`Deleted ${res.data.deleted?.media_files || selectedMediaFiles.length} media files (${res.data.freed_formatted || '0 B'} freed)`);
      setConfirmMediaDeleteOpen(false);
      setSelectedMediaFiles([]);
      fetchMedia(mediaPage);
      fetchHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Media deletion failed');
    } finally {
      setDeletingMedia(false);
    }
  };

  // Delete Single Media File
  const handleDeleteSingleFile = async (filename) => {
    try {
      await API.post('/system/cleanup/delete', {
        sections: [],
        media_filenames: [filename],
        delete_all_media: false
      });
      toast.success(`Deleted ${filename}`);
      setSelectedMediaFiles(prev => prev.filter(n => n !== filename));
      fetchMedia(mediaPage);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete file');
    }
  };

  // Clear Cache Action
  const handleClearCache = async (flushBackend = true) => {
    setClearingCache(true);
    try {
      // 1. Flush Frontend in-memory cache
      clearApiCache();
      updateCacheMetrics();

      // 2. Flush Backend transient logs
      if (flushBackend) {
        const res = await API.post('/system/cache/clear');
        toast.success(`Cache purged! Pruned ${res.data.webhook_logs_trimmed || 0} debug logs and ${res.data.temp_files_cleared || 0} temp files.`);
      } else {
        toast.success('In-memory frontend cache cleared successfully');
      }
      updateCacheMetrics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to flush cache');
    } finally {
      setClearingCache(false);
    }
  };

  if (user && !isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6 space-y-3" data-testid="system-health-restricted">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 mb-1">
          <ShieldWarning size={36} weight="duotone" />
        </div>
        <h2 className="text-xl font-bold tracking-tight">Super Admin Access Only</h2>
        <p className="text-muted-foreground text-sm max-w-md">
          System & Health diagnostics, database maintenance, data cleanup, and cache controls are restricted to Super Administrators.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl pb-16" data-testid="system-health-page">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-border">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              System Health & Maintenance
            </h1>
            {healthData && (
              <Badge
                variant="outline"
                className={`text-xs px-2.5 py-0.5 capitalize flex items-center gap-1.5 ${
                  healthData.status === 'healthy'
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                    : healthData.status === 'degraded'
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                    : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${
                  healthData.status === 'healthy' ? 'bg-emerald-500 animate-pulse' : (healthData.status === 'degraded' ? 'bg-amber-500' : 'bg-red-500')
                }`} />
                {healthData.status}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time server diagnostics, database telemetry, selective data cleanup, media inspector, and cache management
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (activeTab === 'health') fetchHealth();
              else if (activeTab === 'cleanup') fetchCleanupStats();
              else if (activeTab === 'media') fetchMedia(mediaPage);
              else if (activeTab === 'cache') updateCacheMetrics();
            }}
            disabled={loadingHealth || loadingStats || loadingMedia}
            className="gap-1.5 text-xs h-9"
          >
            <ArrowClockwise size={15} className={loadingHealth || loadingStats || loadingMedia ? 'animate-spin' : ''} />
            Refresh
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => handleClearCache(true)}
            disabled={clearingCache}
            className="gap-1.5 text-xs h-9 bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Broom size={15} />
            Quick Purge Cache
          </Button>
        </div>
      </div>

      {/* Main Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full max-w-2xl bg-muted/60 p-1 rounded-xl">
          <TabsTrigger value="health" className="gap-2 text-xs sm:text-sm">
            <Heartbeat size={17} weight="duotone" /> Health Check
          </TabsTrigger>
          <TabsTrigger value="cleanup" className="gap-2 text-xs sm:text-sm">
            <Trash size={17} weight="duotone" /> Data Cleanup
          </TabsTrigger>
          <TabsTrigger value="media" className="gap-2 text-xs sm:text-sm">
            <ImageIcon size={17} weight="duotone" /> Media Files
          </TabsTrigger>
          <TabsTrigger value="cache" className="gap-2 text-xs sm:text-sm">
            <Broom size={17} weight="duotone" /> Clear Cache
          </TabsTrigger>
        </TabsList>

        {/* ========================================================================= */}
        {/* TAB 1: HEALTH STATUS CHECK */}
        {/* ========================================================================= */}
        <TabsContent value="health" className="space-y-6">
          {loadingHealth && !healthData ? (
            <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-3">
              <ArrowClockwise size={28} className="animate-spin text-primary" />
              <span>Running system diagnostics...</span>
            </div>
          ) : (
            <>
              {/* Top Overview Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* 1. Database Metric Card */}
                <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-3 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                        <Database size={22} weight="duotone" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">MongoDB Database</h3>
                        <p className="text-xs text-muted-foreground">{healthData?.database?.db_name || 'Active'}</p>
                      </div>
                    </div>
                    <Badge variant={healthData?.database?.status === 'healthy' ? 'default' : 'destructive'} className="text-[11px]">
                      {healthData?.database?.status || 'Active'}
                    </Badge>
                  </div>

                  <div className="pt-2 grid grid-cols-2 gap-2 text-xs border-t border-border/60">
                    <div>
                      <span className="text-muted-foreground">Ping Latency:</span>
                      <p className="font-mono font-semibold text-foreground text-sm">{healthData?.database?.ping_ms ?? '--'} ms</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Collections:</span>
                      <p className="font-semibold text-foreground text-sm">{healthData?.database?.collections ?? '--'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Data Size:</span>
                      <p className="font-semibold text-foreground">{healthData?.database?.data_size_formatted || '--'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Storage on Disk:</span>
                      <p className="font-semibold text-foreground">{healthData?.database?.storage_size_formatted || '--'}</p>
                    </div>
                  </div>

                  <div className="pt-2 flex justify-between items-center">
                    <span className="text-[11px] text-muted-foreground">Indexed size: {healthData?.database?.index_size_formatted || '0 B'}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePing('mongodb')}
                      disabled={pingingTarget === 'mongodb'}
                      className="text-xs h-7 px-2.5 gap-1 text-primary hover:bg-primary/10"
                    >
                      <Lightning size={13} />
                      {pingingTarget === 'mongodb' ? 'Testing...' : 'Ping DB'}
                    </Button>
                  </div>
                </div>

                {/* 2. Server & Runtime Card */}
                <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-3 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center">
                        <Cpu size={22} weight="duotone" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">Server Runtime</h3>
                        <p className="text-xs text-muted-foreground">FastAPI Engine</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[11px] bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20">
                      Uptime
                    </Badge>
                  </div>

                  <div className="pt-2 grid grid-cols-2 gap-2 text-xs border-t border-border/60">
                    <div>
                      <span className="text-muted-foreground">Server Uptime:</span>
                      <p className="font-semibold text-foreground text-sm flex items-center gap-1">
                        <Clock size={14} className="text-primary" /> {healthData?.server?.uptime_formatted || '--'}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Python Engine:</span>
                      <p className="font-semibold text-foreground text-sm">v{healthData?.server?.python_version || '--'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Platform / OS:</span>
                      <p className="font-mono text-xs text-foreground truncate">{healthData?.server?.platform || '--'}</p>
                    </div>
                  </div>

                  <div className="pt-2 flex justify-between items-center text-[11px] text-muted-foreground">
                    <span>Started: {healthData?.server?.started_at?.replace('T', ' ').slice(0, 16) || '--'}</span>
                    <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium">
                      <CheckCircle size={13} /> Active
                    </span>
                  </div>
                </div>

                {/* 3. Disk & Storage Card */}
                <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-3 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                        <HardDrives size={22} weight="duotone" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">Disk & Storage</h3>
                        <p className="text-xs text-muted-foreground">Uploads & Files</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[11px] ${
                      healthData?.storage?.disk_usage_percent < 85
                        ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                    }`}>
                      {healthData?.storage?.disk_usage_percent || 0}% used
                    </Badge>
                  </div>

                  {/* Disk Usage Progress */}
                  <div className="space-y-1.5 pt-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Disk Usage:</span>
                      <span className="font-semibold text-foreground">{healthData?.storage?.disk_used_formatted} / {healthData?.storage?.disk_total_formatted}</span>
                    </div>
                    <Progress value={healthData?.storage?.disk_usage_percent || 0} className="h-2" />
                  </div>

                  <div className="pt-2 grid grid-cols-2 gap-2 text-xs border-t border-border/60">
                    <div>
                      <span className="text-muted-foreground">Free Space:</span>
                      <p className="font-semibold text-foreground">{healthData?.storage?.disk_free_formatted || '--'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Media Uploads:</span>
                      <p className="font-semibold text-foreground">{healthData?.storage?.total_files || 0} files ({healthData?.storage?.total_media_formatted || '0 B'})</p>
                    </div>
                  </div>

                  <div className="pt-2 flex justify-between items-center">
                    <span className="text-[11px] text-muted-foreground truncate max-w-[140px]" title={healthData?.storage?.upload_dir}>
                      /uploads directory
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePing('storage')}
                      disabled={pingingTarget === 'storage'}
                      className="text-xs h-7 px-2.5 gap-1 text-primary hover:bg-primary/10"
                    >
                      <Lightning size={13} />
                      {pingingTarget === 'storage' ? 'Testing...' : 'Test I/O'}
                    </Button>
                  </div>
                </div>

                {/* 4. WhatsApp System Health */}
                <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-3 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-lg bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center">
                        <WhatsappLogo size={22} weight="fill" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">WhatsApp Cloud API</h3>
                        <p className="text-xs text-muted-foreground">WABA & Webhooks</p>
                      </div>
                    </div>
                    <Badge variant={healthData?.whatsapp?.status === 'healthy' ? 'default' : 'outline'} className="text-[11px]">
                      {healthData?.whatsapp?.status === 'healthy' ? 'Connected' : (healthData?.whatsapp?.total_apis > 0 ? 'Active' : 'No WABA')}
                    </Badge>
                  </div>

                  <div className="pt-2 grid grid-cols-2 gap-2 text-xs border-t border-border/60">
                    <div>
                      <span className="text-muted-foreground">APIs Configured:</span>
                      <p className="font-semibold text-foreground text-sm">
                        {healthData?.whatsapp?.active_apis || 0} active <span className="text-muted-foreground text-xs font-normal">/ {healthData?.whatsapp?.total_apis || 0}</span>
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Open Chats:</span>
                      <p className="font-semibold text-foreground text-sm">{healthData?.whatsapp?.open_contacts || 0} active</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total Messages:</span>
                      <p className="font-semibold text-foreground">{healthData?.whatsapp?.total_messages || 0}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last Send Status:</span>
                      <p className={`font-semibold ${healthData?.whatsapp?.last_send_ok ? 'text-emerald-600' : 'text-foreground'}`}>
                        {healthData?.whatsapp?.last_send_ok === true ? 'Delivered' : (healthData?.whatsapp?.last_send_ok === false ? 'Warning' : 'Idle')}
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 flex justify-between items-center">
                    <span className="text-[11px] text-muted-foreground">
                      {healthData?.whatsapp?.last_incoming_at ? `Last In: ${healthData.whatsapp.last_incoming_at.replace('T', ' ').slice(11, 19)}` : 'No incoming logs'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePing('whatsapp')}
                      disabled={pingingTarget === 'whatsapp'}
                      className="text-xs h-7 px-2.5 gap-1 text-primary hover:bg-primary/10"
                    >
                      <Lightning size={13} />
                      {pingingTarget === 'whatsapp' ? 'Testing...' : 'Ping WABA'}
                    </Button>
                  </div>
                </div>

                {/* 5. Telegram Bot System Health */}
                <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-3 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center">
                        <TelegramLogo size={22} weight="fill" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">Telegram Service</h3>
                        <p className="text-xs text-muted-foreground">Multi-Bot Engine</p>
                      </div>
                    </div>
                    <Badge variant={healthData?.telegram?.api_connected ? 'default' : 'destructive'} className="text-[11px]">
                      {healthData?.telegram?.api_connected ? 'Online' : 'Degraded'}
                    </Badge>
                  </div>

                  <div className="pt-2 grid grid-cols-2 gap-2 text-xs border-t border-border/60">
                    <div>
                      <span className="text-muted-foreground">Bots Registered:</span>
                      <p className="font-semibold text-foreground text-sm">
                        {healthData?.telegram?.active_bots || 0} active <span className="text-muted-foreground text-xs font-normal">/ {healthData?.telegram?.total_bots || 0}</span>
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Bot Users:</span>
                      <p className="font-semibold text-foreground text-sm">{healthData?.telegram?.total_users || 0}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Bot Messages:</span>
                      <p className="font-semibold text-foreground">{healthData?.telegram?.total_messages || 0}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">API Latency:</span>
                      <p className="font-mono font-semibold text-foreground">{healthData?.telegram?.api_latency_ms ?? '--'} ms</p>
                    </div>
                  </div>

                  <div className="pt-2 flex justify-between items-center">
                    <span className="text-[11px] text-muted-foreground">api.telegram.org</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePing('telegram')}
                      disabled={pingingTarget === 'telegram'}
                      className="text-xs h-7 px-2.5 gap-1 text-primary hover:bg-primary/10"
                    >
                      <Lightning size={13} />
                      {pingingTarget === 'telegram' ? 'Testing...' : 'Ping Telegram'}
                    </Button>
                  </div>
                </div>

                {/* 6. Background Workers Health */}
                <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-3 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                        <Sparkle size={22} weight="duotone" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">Background Workers</h3>
                        <p className="text-xs text-muted-foreground">Async Schedulers</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[11px] bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20">
                      Running
                    </Badge>
                  </div>

                  <div className="pt-2 space-y-2 text-xs border-t border-border/60">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Scheduled Broadcast Checker:</span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                        <CheckCircle size={13} /> Active
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Auto-Messages Scheduler:</span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                        <CheckCircle size={13} /> Active
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Channel Broadcast Worker:</span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                        <CheckCircle size={13} /> Active
                      </span>
                    </div>
                  </div>

                  <div className="pt-1 text-[11px] text-muted-foreground">
                    All background jobs operational without bottlenecks
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* ========================================================================= */}
        {/* TAB 2: DATA CLEANUP SECTION */}
        {/* ========================================================================= */}
        <TabsContent value="cleanup" className="space-y-6">
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs sm:text-sm text-amber-900 dark:text-amber-200 flex items-start gap-3">
            <ShieldWarning size={22} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm">Selective Data Cleanup Notice</p>
              <p className="opacity-90 mt-0.5">
                Select the individual database sections you wish to prune or purge. By default, open active customer chats on WhatsApp will be preserved to maintain uninterrupted customer relationships.
              </p>
            </div>
          </div>

          {/* Controls Bar: Retention Filter & Select All */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border border-border bg-card">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Prune Period:</span>
              <div className="flex items-center gap-1 bg-muted/60 p-1 rounded-lg">
                {[
                  { id: 'all', label: 'All Records' },
                  { id: '90', label: 'Older than 90 Days' },
                  { id: '30', label: 'Older than 30 Days' },
                  { id: '7', label: 'Older than 7 Days' },
                ].map(r => (
                  <button
                    key={r.id}
                    onClick={() => setRetentionDays(r.id)}
                    className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                      retentionDays === r.id ? 'bg-background text-foreground font-semibold shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAllSections}
                className="text-xs h-8"
              >
                {cleanupStats?.sections && selectedSections.length === Object.keys(cleanupStats.sections).length ? 'Deselect All' : 'Select All Sections'}
              </Button>
            </div>
          </div>

          {/* Section Selection Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cleanupStats?.sections && Object.entries(cleanupStats.sections).map(([key, item]) => {
              const isSelected = selectedSections.includes(key);
              return (
                <div
                  key={key}
                  onClick={() => toggleSection(key)}
                  className={`p-4 rounded-xl border cursor-pointer transition-all ${
                    isSelected
                      ? 'border-red-500/50 bg-red-500/5 dark:bg-red-500/10 shadow-sm'
                      : 'border-border bg-card hover:border-border/80'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSection(key)}
                        className="data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                      />
                      <span className="font-semibold text-sm">{item.name}</span>
                    </div>
                    <Badge variant="outline" className={`font-mono text-xs ${isSelected ? 'border-red-500/40 text-red-600 dark:text-red-400' : ''}`}>
                      {item.count.toLocaleString()}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 pl-6">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Safety Settings & Execute Action */}
          <div className="p-4 rounded-xl border border-border bg-card flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Switch
                id="keep-open-switch"
                checked={keepOpenContacts}
                onCheckedChange={setKeepOpenContacts}
              />
              <Label htmlFor="keep-open-switch" className="text-xs sm:text-sm cursor-pointer">
                <span className="font-semibold">Protect Open WhatsApp Chats</span> (Only delete closed WhatsApp contacts and their history)
              </Label>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <div className="text-right text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{selectedSections.length}</span> sections selected (~<span className="font-semibold text-foreground">{totalItemsToDelete.toLocaleString()}</span> items)
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirmDialogOpen(true)}
                disabled={selectedSections.length === 0 || deletingData}
                className="gap-1.5 text-xs h-9 px-4"
              >
                <Trash size={15} />
                Delete Selected Data
              </Button>
            </div>
          </div>

          {/* Confirmation Modal */}
          <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <Warning size={20} /> Confirm Permanent Data Deletion
                </DialogTitle>
                <DialogDescription className="text-xs">
                  This action is permanent and cannot be undone. Are you sure you want to delete data from the following sections?
                </DialogDescription>
              </DialogHeader>

              <div className="py-2 space-y-2">
                <div className="p-3 rounded-lg bg-muted text-xs space-y-1">
                  <p className="font-semibold text-foreground">Selected Sections ({selectedSections.length}):</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                    {selectedSections.map(s => (
                      <li key={s}>{cleanupStats?.sections?.[s]?.name || s} ({cleanupStats?.sections?.[s]?.count || 0} records)</li>
                    ))}
                  </ul>
                  <p className="pt-1 text-[11px] text-muted-foreground border-t mt-2">
                    Filter: {retentionDays === 'all' ? 'All records of all time' : `Records older than ${retentionDays} days`}
                  </p>
                </div>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" size="sm" onClick={() => setConfirmDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleExecuteCleanup}
                  disabled={deletingData}
                  className="gap-1.5"
                >
                  {deletingData ? <ArrowClockwise size={15} className="animate-spin" /> : <Trash size={15} />}
                  Confirm & Delete Now
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ========================================================================= */}
        {/* TAB 3: MEDIA BROWSER & SELECTIVE DELETION */}
        {/* ========================================================================= */}
        <TabsContent value="media" className="space-y-6">
          {/* Media Breakdown Summary Cards */}
          {cleanupStats?.media_summary && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {[
                { cat: 'images', label: 'Photos & Images', icon: ImageIcon, color: 'text-emerald-500' },
                { cat: 'documents', label: 'Documents', icon: FileText, color: 'text-blue-500' },
                { cat: 'videos', label: 'Videos', icon: VideoCamera, color: 'text-purple-500' },
                { cat: 'audio', label: 'Audio & Voice', icon: MusicNotes, color: 'text-amber-500' },
                { cat: 'other', label: 'Other Files', icon: HardDrives, color: 'text-gray-500' },
              ].map(item => {
                const b = cleanupStats.media_summary.breakdown[item.cat] || { count: 0, formatted: '0 B' };
                const Icon = item.icon;
                return (
                  <div key={item.cat} className="p-3.5 rounded-xl border border-border bg-card flex flex-col justify-between">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{item.label}</span>
                      <Icon size={16} className={item.color} />
                    </div>
                    <div className="mt-2">
                      <p className="text-lg font-bold text-foreground font-mono">{b.count}</p>
                      <p className="text-[11px] text-muted-foreground">{b.formatted}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Media Filtering & Search Bar */}
          <div className="p-4 rounded-xl border border-border bg-card space-y-3">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
              {/* Category Filter Pills */}
              <div className="flex flex-wrap items-center gap-1.5">
                {[
                  { id: 'all', label: 'All Media' },
                  { id: 'image', label: 'Images' },
                  { id: 'document', label: 'Documents' },
                  { id: 'video', label: 'Videos' },
                  { id: 'audio', label: 'Audio' },
                ].map(c => (
                  <button
                    key={c.id}
                    onClick={() => { setMediaCategory(c.id); setMediaPage(1); }}
                    className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                      mediaCategory === c.id ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted/60 text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              {/* Search & Source filter */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 sm:w-56">
                  <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search filename..."
                    value={mediaSearch}
                    onChange={e => { setMediaSearch(e.target.value); setMediaPage(1); }}
                    className="pl-8 text-xs h-8"
                  />
                </div>
                <select
                  value={mediaSource}
                  onChange={e => { setMediaSource(e.target.value); setMediaPage(1); }}
                  className="text-xs h-8 px-2 rounded-md border border-input bg-background"
                >
                  <option value="all">All Sources</option>
                  <option value="whatsapp">WhatsApp Only</option>
                  <option value="telegram">Telegram Only</option>
                </select>
                <select
                  value={mediaSort}
                  onChange={e => { setMediaSort(e.target.value); setMediaPage(1); }}
                  className="text-xs h-8 px-2 rounded-md border border-input bg-background"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="largest">Largest Size</option>
                  <option value="smallest">Smallest Size</option>
                </select>
              </div>
            </div>

            {/* Selection & Batch Actions Row */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border/60 text-xs">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAllMediaOnPage}
                  className="text-xs h-7 px-2.5"
                >
                  {mediaList.length > 0 && mediaList.every(m => selectedMediaFiles.includes(m.name)) ? 'Deselect Page' : 'Select All On Page'}
                </Button>
                {selectedMediaFiles.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedMediaFiles([])}
                    className="text-xs h-7 px-2"
                  >
                    Clear Selection
                  </Button>
                )}
                <span className="text-muted-foreground">
                  Selected: <span className="font-semibold text-foreground">{selectedMediaFiles.length}</span> files ({selectedMediaSizeFormatted})
                </span>
              </div>

              {selectedMediaFiles.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setConfirmMediaDeleteOpen(true)}
                  className="text-xs h-8 gap-1.5 bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash size={14} />
                  Delete Selected ({selectedMediaFiles.length})
                </Button>
              )}
            </div>
          </div>

          {/* Media Grid Gallery */}
          {loadingMedia ? (
            <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-3">
              <ArrowClockwise size={24} className="animate-spin text-primary" />
              <span>Loading media library...</span>
            </div>
          ) : mediaList.length === 0 ? (
            <div className="border border-dashed border-border rounded-xl p-12 text-center text-muted-foreground">
              <ImageIcon size={36} className="mx-auto text-muted-foreground/50 mb-2" />
              <p className="font-medium">No media files found</p>
              <p className="text-xs text-muted-foreground mt-1">Try adjusting your filters or upload some files.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {mediaList.map((m) => {
                const isSelected = selectedMediaFiles.includes(m.name);
                const fullUrl = resolveMediaUrl(m.url);
                return (
                  <div
                    key={m.name}
                    className={`group relative rounded-xl border bg-card overflow-hidden shadow-sm flex flex-col transition-all ${
                      isSelected ? 'border-red-500 ring-2 ring-red-500/20' : 'border-border hover:border-primary/40'
                    }`}
                  >
                    {/* Top Checkbox Overlay */}
                    <div className="absolute top-2 left-2 z-10">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleMediaFile(m.name)}
                        className="data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600 shadow"
                      />
                    </div>

                    {/* Single Delete Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteSingleFile(m.name); }}
                      title="Delete this file"
                      className="absolute top-2 right-2 z-10 w-6 h-6 rounded-md bg-black/60 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash size={12} />
                    </button>

                    {/* Thumbnail / Media Container */}
                    <div
                      className="h-28 bg-muted/40 relative flex items-center justify-center cursor-pointer overflow-hidden"
                      onClick={() => setPreviewMediaModal(m)}
                    >
                      {m.category === 'image' ? (
                        <img
                          src={fullUrl}
                          alt={m.name}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : m.category === 'video' ? (
                        <div className="flex flex-col items-center gap-1 text-purple-600">
                          <VideoCamera size={32} weight="duotone" />
                          <span className="text-[10px] uppercase font-mono font-bold">VIDEO</span>
                        </div>
                      ) : m.category === 'audio' ? (
                        <div className="flex flex-col items-center gap-1 text-amber-600">
                          <MusicNotes size={32} weight="duotone" />
                          <span className="text-[10px] uppercase font-mono font-bold">AUDIO</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1 text-blue-600">
                          <FileText size={32} weight="duotone" />
                          <span className="text-[10px] uppercase font-mono font-bold">{m.extension || 'DOC'}</span>
                        </div>
                      )}
                    </div>

                    {/* Metadata Card Footer */}
                    <div className="p-2.5 flex-1 flex flex-col justify-between text-xs space-y-1">
                      <p className="font-medium text-foreground truncate text-[11px]" title={m.name}>
                        {m.name}
                      </p>
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                        <span className="font-mono">{m.size_formatted}</span>
                        <span className="truncate max-w-[80px]">{m.source}</span>
                      </div>
                      <div className="flex items-center justify-between pt-1 border-t border-border/40 text-[10px]">
                        <span className="text-muted-foreground">{m.created_at?.slice(5, 10)}</span>
                        <a
                          href={fullUrl}
                          download={m.name}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline flex items-center gap-0.5"
                          onClick={e => e.stopPropagation()}
                        >
                          <DownloadSimple size={11} /> Save
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination Controls */}
          {mediaTotalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-border text-xs text-muted-foreground">
              <span>Showing {mediaList.length} of {mediaTotal} media files</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchMedia(mediaPage - 1)}
                  disabled={mediaPage <= 1 || loadingMedia}
                  className="h-8 text-xs"
                >
                  Previous
                </Button>
                <span className="font-medium text-foreground">Page {mediaPage} of {mediaTotalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchMedia(mediaPage + 1)}
                  disabled={mediaPage >= mediaTotalPages || loadingMedia}
                  className="h-8 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* Full Media Preview Modal */}
          {previewMediaModal && (
            <Dialog open={Boolean(previewMediaModal)} onOpenChange={() => setPreviewMediaModal(null)}>
              <DialogContent className="max-w-3xl p-4 bg-background border border-border">
                <DialogHeader className="flex flex-row items-center justify-between pb-2 border-b">
                  <DialogTitle className="text-sm truncate font-medium">
                    {previewMediaModal.name}
                  </DialogTitle>
                  <div className="flex items-center gap-2">
                    <a
                      href={resolveMediaUrl(previewMediaModal.url)}
                      download={previewMediaModal.name}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs px-2.5 py-1 rounded bg-primary text-primary-foreground flex items-center gap-1 font-medium hover:opacity-90"
                    >
                      <DownloadSimple size={14} /> Download
                    </a>
                  </div>
                </DialogHeader>

                <div className="flex items-center justify-center min-h-[300px] max-h-[70vh] overflow-auto py-3">
                  {previewMediaModal.category === 'image' ? (
                    <img src={resolveMediaUrl(previewMediaModal.url)} alt="" className="max-w-full max-h-[65vh] object-contain rounded-md" />
                  ) : previewMediaModal.category === 'video' ? (
                    <video src={resolveMediaUrl(previewMediaModal.url)} controls className="max-w-full max-h-[65vh] rounded-md" />
                  ) : previewMediaModal.category === 'audio' ? (
                    <audio src={resolveMediaUrl(previewMediaModal.url)} controls className="w-full max-w-md" />
                  ) : (
                    <div className="p-8 text-center space-y-3">
                      <FileText size={64} className="mx-auto text-blue-500" />
                      <p className="font-semibold text-sm">{previewMediaModal.name}</p>
                      <p className="text-xs text-muted-foreground">{previewMediaModal.size_formatted}</p>
                      <Button
                        size="sm"
                        onClick={() => window.open(resolveMediaUrl(previewMediaModal.url), '_blank')}
                        className="gap-1.5 text-xs"
                      >
                        <ArrowSquareOut size={14} /> Open Document
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Confirm Media Delete Modal */}
          <Dialog open={confirmMediaDeleteOpen} onOpenChange={setConfirmMediaDeleteOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <Warning size={20} /> Delete {selectedMediaFiles.length} Media Files?
                </DialogTitle>
                <DialogDescription className="text-xs">
                  The selected media files will be permanently deleted from the server storage ({selectedMediaSizeFormatted} freed).
                </DialogDescription>
              </DialogHeader>

              <div className="max-h-48 overflow-y-auto p-2 rounded-lg bg-muted text-xs space-y-1">
                {selectedMediaFiles.map(f => (
                  <p key={f} className="truncate font-mono">{f}</p>
                ))}
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" size="sm" onClick={() => setConfirmMediaDeleteOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleExecuteMediaDelete}
                  disabled={deletingMedia}
                  className="gap-1.5"
                >
                  {deletingMedia ? <ArrowClockwise size={15} className="animate-spin" /> : <Trash size={15} />}
                  Delete Files
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ========================================================================= */}
        {/* TAB 4: CLEAR CACHE SECTION */}
        {/* ========================================================================= */}
        <TabsContent value="cache" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 1. Frontend In-Memory Cache Card */}
            <div className="p-5 rounded-xl border border-border bg-card shadow-sm space-y-3 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Sparkle size={22} weight="duotone" />
                  </div>
                  <Badge variant="outline" className="text-xs">Frontend</Badge>
                </div>
                <h3 className="font-semibold text-sm mt-3">In-Memory API Cache</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Caches instant read responses and in-flight API deduplications to make navigation blazing fast.
                </p>
                <div className="mt-3 text-xs space-y-1 font-mono">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cached Queries:</span>
                    <span className="font-semibold text-foreground">{cacheStats.cachedRequests}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">In-Flight Requests:</span>
                    <span className="font-semibold text-foreground">{cacheStats.inFlightRequests}</span>
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleClearCache(false)}
                className="w-full text-xs h-9 gap-1.5"
              >
                <Broom size={14} /> Clear Client Memory
              </Button>
            </div>

            {/* 2. Webhook Logs & Diagnostics Cache */}
            <div className="p-5 rounded-xl border border-border bg-card shadow-sm space-y-3 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                    <FileText size={22} weight="duotone" />
                  </div>
                  <Badge variant="outline" className="text-xs">Backend</Badge>
                </div>
                <h3 className="font-semibold text-sm mt-3">Webhook Debug Logs</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Prunes transient raw webhook payloads older than 24 hours while keeping recent diagnostics intact.
                </p>
                <div className="mt-3 text-xs space-y-1 font-mono">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Retention Policy:</span>
                    <span className="font-semibold text-foreground">Last 24 Hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Log Truncation:</span>
                    <span className="font-semibold text-emerald-600">Auto-capped</span>
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleClearCache(true)}
                disabled={clearingCache}
                className="w-full text-xs h-9 gap-1.5"
              >
                <Broom size={14} /> Prune Transient Logs
              </Button>
            </div>

            {/* 3. Deduplication & Optimization Card */}
            <div className="p-5 rounded-xl border border-border bg-card shadow-sm space-y-3 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                    <ShieldCheck size={22} weight="duotone" />
                  </div>
                  <Badge variant="outline" className="text-xs">Optimization</Badge>
                </div>
                <h3 className="font-semibold text-sm mt-3">Thread Deduplication</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Re-scans WhatsApp contacts globally and merges any split threads into continuous conversations.
                </p>
                <div className="mt-3 text-xs space-y-1 font-mono">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Contact Threads:</span>
                    <span className="font-semibold text-foreground">Single Global per Mobile</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Temp Files:</span>
                    <span className="font-semibold text-foreground">Auto-cleaned</span>
                  </div>
                </div>
              </div>

              <Button
                variant="default"
                size="sm"
                onClick={() => handleClearCache(true)}
                disabled={clearingCache}
                className="w-full text-xs h-9 gap-1.5 bg-primary text-primary-foreground"
              >
                <Lightning size={14} /> Optimize & Flush All
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
